import { useEffect, useState } from 'react';
import { Loader2, AlertCircle, FileText } from 'lucide-react';
import { api } from '../services/api';

interface Position {
  id: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  label: string | null;
}

interface PdfViewerProps {
  documentId: string;
  signerId?: string;
  page: number;
  positions?: Position[];
  highlightPositions?: boolean;
}

/**
 * Renders a PDF preview using the browser's built-in PDF renderer via an
 * object URL. The file is fetched through the API (auth-protected, or via
 * the signer public token) so R2 stays private.
 */
export default function PdfViewer({
  documentId,
  signerId,
  page,
  positions = [],
  highlightPositions = false,
}: PdfViewerProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const endpoint = signerId
          ? `/signers/${signerId}/file`
          : `/documents/${documentId}/view`;

        const response = await api.get(endpoint, { responseType: 'blob' });
        if (cancelled) return;

        // Reject JSON error payloads disguised as blobs
        if (response.data.type === 'application/json') {
          const text = await response.data.text();
          throw new Error(JSON.parse(text)?.error?.message || 'Không thể tải PDF');
        }

        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        revoked = url;
        setObjectUrl(url);
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.error?.message || err.message || 'Không thể tải PDF');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [documentId, signerId]);

  const pagePositions = positions.filter((p) => p.page === page);

  return (
    <div>
      {pagePositions.length > 0 && highlightPositions && (
        <div className="mb-3 flex flex-wrap gap-2">
          {pagePositions.map((pos) => (
            <span
              key={pos.id}
              className="badge badge-warning"
              title={`Vị trí ký: ${pos.label || 'Chữ ký'}`}
            >
              {pos.label || 'Chữ ký'} · Trang {pos.page}
            </span>
          ))}
        </div>
      )}

      <div className="relative bg-slate-100 dark:bg-slate-950/80 rounded-xl overflow-hidden border border-slate-200 dark:border-white/10" style={{ height: '650px' }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-950/90 z-10">
            <div className="text-center">
              <Loader2 className="w-7 h-7 text-sky-500 animate-spin mx-auto mb-2" />
              <p className="text-xs text-slate-500 dark:text-slate-400 font-normal">Đang chuẩn bị bản xem trước PDF...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/90 dark:bg-slate-950/90 p-6 z-10">
            <div className="text-center max-w-sm">
              <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2.5" />
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-3 font-normal">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="btn-outline text-xs h-7 px-3 font-normal"
              >
                Thử lại
              </button>
            </div>
          </div>
        )}

        {!loading && !error && objectUrl && (
          <iframe
            src={`${objectUrl}#page=${page}&toolbar=1&navpanes=0&view=FitH`}
            title="PDF document preview"
            className="w-full h-full border-0 rounded-xl bg-white dark:bg-slate-900"
          />
        )}

        {!loading && !error && !objectUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-xs text-slate-400">Không có dữ liệu hiển thị PDF</p>
            </div>
          </div>
        )}
      </div>

      <p className="text-[11px] text-slate-400 mt-2 font-normal">
        * Nếu trình duyệt chặn hiển thị khung nội tuyến PDF, vui lòng sử dụng nút "Tải xuống" để xem trực tiếp trên máy.
      </p>
    </div>
  );
}